import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelope, FaMapMarkerAlt, FaExclamationTriangle, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isImportantInfoOpen, setIsImportantInfoOpen] = useState(false);

  const handleAnchorClick = (e, anchor) => {
    e.preventDefault();
    const element = document.getElementById(anchor.replace('#', ''));
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const toggleImportantInfo = () => {
    setIsImportantInfoOpen(!isImportantInfoOpen);
  };
  
  return (
    <footer className="bg-gray-50 text-gray-900">
      <div className="container-custom py-12">
        
        {/* Main Footer - Strict Left Alignment */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8 text-left">
          
          {/* Company Info - Column 1 */}
          <div>
            <div className="mb-6">
              <Link to="/" className="inline-block mb-6">
                <img 
                  src="https://res.cloudinary.com/dqwxmksbe/image/upload/v1762423871/blockfirstcapital-dark-logo_dcbbka.png" 
                  alt="Blockfirst Capital" 
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            
            <div className="mb-6 text-left">
              <div className="mb-3">
                <div className="flex items-start">
                  <FaExclamationTriangle className="text-yellow-600 mr-2 mt-1 text-sm" />
                  <h4 className="text-sm font-semibold text-gray-900">Risk Disclosure</h4>
                </div>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed text-left">
                Investments with Blockfirst Capital are designed for experienced investors seeking diverse portfolio exposure. While all investments carry inherent risks, our professional management team employs sophisticated strategies to optimize returns while maintaining prudent risk controls aligned with your financial objectives.
              </p>
            </div>
          </div>
          
          {/* UK Office - Column 2 */}
          <div className="text-left">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-left">UK Office</h4>
            <div className="space-y-3">
              <div className="text-left">
                <div className="flex items-start">
                  <FaMapMarkerAlt className="text-gray-700 mt-1 mr-2 text-sm" />
                  <div className="text-sm text-gray-600 leading-relaxed text-left">
                    <div>Bureau, 90 Fetter Lane</div>
                    <div>London, United Kingdom</div>
                    <div>EC4A 1EN</div>
                  </div>
                </div>
              </div>
              <div className="text-left">
                <div className="flex items-start">
                  <FaEnvelope className="text-gray-700 mt-1 mr-2 text-sm" />
                  <span className="text-sm text-gray-600 text-left">info@blockfirstcapital.com</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Legal & Documents - Column 4 */}
          <div className="text-left">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-left">Legal & Documents</h4>
            

            
            <div className="text-left">
              <ul className="space-y-2 text-left">
                <li>
                  <a 
                    href="https://find-and-update.company-information.service.gov.uk/company/09852426"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200 text-left block"
                  >
                    Certificate of Incorporation
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
        </div>
        
        {/* Collapsible Important Investment Information Section */}
        <div className="border-t border-gray-200 pt-6 text-left">
          <button
            onClick={toggleImportantInfo}
            className="flex items-center justify-between w-full text-left hover:text-gray-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 rounded p-2"
            id="risk-warning"
          >
            <div className="flex items-center">
              <FaInfoCircle className="text-yellow-600 mr-3 text-lg" />
              <h4 className="text-lg font-semibold text-gray-900 text-left">Important Investment Information</h4>
            </div>
            <div className="flex items-center ml-4">
              {isImportantInfoOpen ? (
                <FaChevronUp className="text-gray-500 text-sm" />
              ) : (
                <FaChevronDown className="text-gray-500 text-sm" />
              )}
            </div>
          </button>
          
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isImportantInfoOpen ? 'max-h-screen md:max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}>
            <div className="text-left pl-4 md:pl-8">
              <div className="space-y-4 text-left">
                <p className="text-sm md:text-sm text-gray-600 leading-relaxed text-left">
                  Please consider your objectives before investing. Past performance is not indicative of future results. Forward-looking statements, hypothetical information or calculations, financial estimates, assumptions, or estimates are inherently uncertain. Such information should not be used as a basis for an investor's decision to invest. Securities offered by Blockfirst Capital Securities, LLC on the Platform, including investments in the Financial market, are speculative, highly illiquid, and involve substantial risk. You should not invest unless you can sustain the risk of loss of capital, including the risk of total loss of capital.
                </p>
                <p className="text-sm md:text-sm text-gray-600 leading-relaxed text-left">
                  The information contained on this site, including attachments, is not to be construed as advice unless specifically referred to as Advice.
                </p>
              </div>
            </div>
          </div>
        </div>
        
      </div>
      
      {/* Bottom Bar */}
      <div className="bg-gray-100 py-4 border-t border-gray-200">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row justify-between items-center text-sm text-gray-600 space-y-2 sm:space-y-0">
            <div className="text-left">
              &copy; {currentYear} Blockfirst Capital. All rights reserved.
            </div>
            <div className="flex flex-row gap-4 justify-center">
              <Link 
                to="/privacy-policy" 
                className="hover:text-gray-900 transition-colors duration-200"
              >
                Privacy
              </Link>
              <Link 
                to="/terms-of-service" 
                className="hover:text-gray-900 transition-colors duration-200"
              >
                Terms
              </Link>
              <button 
                onClick={toggleImportantInfo}
                className="hover:text-gray-900 transition-colors duration-200 cursor-pointer"
              >
                Important Info
              </button>
            </div>
          </div>
        </div>
      </div>
      
    </footer>
  );
};

export default Footer; 